import React, { useEffect, useState, useRef, useCallback } from 'react';
import LoadingSpinner from '@/components/common/elements/LoadingSpinner';
import Pagination from '../elements/Pagination';
import { TableProps } from '../elements/Table';
import { api } from '@/api/repository/axios';
import { useListRestore, useScrollRestore } from '@/common/listRestore';
import PageState from '@/components/common/PageState';
import { FORM_COLOR } from '@/pages/Seminar/components/form/formStyles';
import styled from '@emotion/styled';

interface TableListProps<T extends { id: number | string }> {
    headers: string[];
    listCount: number;
    windowSize?: number;
    fetchUrl?: string;
    list?: T[];
    onClick: (id: T['id']) => void;
    InnerTable: (props: TableProps<T>) => React.JSX.Element;
    onInitialDataFetched?: (response: any) => void;
    /** fetch 완료 후 결과 0건일 때 노출할 메시지. 미지정 시 노출 안 함. */
    emptyMessage?: React.ReactNode;
    /** 전체 목록 화면은 page, 마이페이지 내부 위젯은 inline. 더보기는 항상 inline. */
    loadingPlacement?: 'page' | 'inline';
    /** 바깥 레이아웃에서 여백을 제공하면 false. 기존 목록은 기본 여백 유지. */
    bottomSpacer?: boolean;
}

/** 뒤로가기로 돌아왔을 때 되살릴 목록 상태 (common/listRestore 참고) */
interface TableListSnapshot<T> {
    requestData: T[];
    currentPage: number;
    totalPages: number;
    hasMore: boolean;
    /** 첫 페이지 응답 — 복원 시에도 onInitialDataFetched 를 다시 불러 화면의 파생 상태를 채운다 */
    initialResult: unknown;
}

const LoadingIndicator = styled.div`
    text-align: center;
    padding: 20px;
    color: #666;
    font-size: 0.9rem;
`;

/** 글자만 넘긴 빈 안내 — 공용 빈 상태(PageState)와 같은 15px 흐림(#66736F) */
const EmptyState = styled.div`
    text-align: center;
    padding: 48px 16px;
    color: ${FORM_COLOR.muted};
    font-size: 15px;
    line-height: 1.6;
    word-break: keep-all;

    /* PageState 같은 안내 카드를 넘긴 경우 — 카드가 자체 여백을 가지므로 위아래 여백을 겹쳐 주지 않는다 */
    &:has(> [role='status']),
    &:has(> [role='alert']) {
        padding: 0;
    }
`;

const PaginationWrapper = styled.div`
    /* 마진은 공용 Pagination 컴포넌트에서 일원화 (모든 목록 페이지 동일) */
    @media (max-width: 768px) {
        display: none;
    }
`;

const TableList = <T extends { id: number | string }>({ headers, listCount, windowSize, fetchUrl, list: initialData, onClick, InnerTable, onInitialDataFetched, emptyMessage, loadingPlacement = 'inline', bottomSpacer = true }: TableListProps<T>) => {
	// 목록에서 글을 보고 뒤로 왔을 때: 다시 받지 않고 보던 목록·페이지·스크롤 위치를 그대로 되살린다.
	// 검색어·필터가 다르면 fetchUrl 이 달라 다른 저장본으로 구분된다. fetchUrl 없는(정적 list) 사용처는 대상이 아니다.
	const { restored, save } = useListRestore<TableListSnapshot<T>>(`table:${fetchUrl ?? ''}`);
	const snapshot: TableListSnapshot<T> | null = fetchUrl && !initialData ? restored : null;
	const [requestData, setRequestData] = useState<T[]>(snapshot?.requestData ?? initialData ?? []);
	const [currentPage, setCurrentPage] = useState(snapshot?.currentPage ?? 0);
	const [totalPages, setTotalPages] = useState(snapshot?.totalPages ?? 0);
	const [isLoading, setIsLoading] = useState(false);
	const [isAppending, setIsAppending] = useState(false);
	const [hasMore, setHasMore] = useState(snapshot?.hasMore ?? true);
	const [isMobile, setIsMobile] = useState(false);
	const [hasFetched, setHasFetched] = useState(snapshot !== null);
	/** 직전 요청이 실패했는지 — 실패를 "0건" 으로 보여주면 사용자가 글이 없는 걸로 오해한다 */
	const [hasError, setHasError] = useState(false);
	// 지금 화면에 그려져 있는 페이지. 요청 중인 currentPage 와 구분한다 —
	// 페이지 이동이 실패하면 화면엔 이전 페이지 내용이 그대로 남는데 페이지네이션만 새 번호를 켜면 사용자가 속는다.
	const [displayedPage, setDisplayedPage] = useState(snapshot?.currentPage ?? 0);
	const observerTarget = useRef<HTMLDivElement>(null);
	const initialResultRef = useRef<unknown>(snapshot?.initialResult ?? null);
	/** 실패한 요청의 (page, append) — "다시 시도" 가 정확히 그 요청을 다시 보내게 한다 */
	const failedRequestRef = useRef<{ page: number; append: boolean } | null>(null);
	/** 복원한 (fetchUrl, page) 조합 — 이 조합인 동안에는 같은 페이지를 다시 받지 않는다. 페이지가 바뀌면 해제 */
	const restoredAtRef = useRef<{ url: string; page: number } | null>(snapshot && fetchUrl ? { url: fetchUrl, page: snapshot.currentPage } : null);
	useScrollRestore(snapshot !== null);

	// 모바일 감지
	useEffect(() => {
		const checkMobile = () => {
			setIsMobile(window.innerWidth <= 768);
		};
		checkMobile();
		window.addEventListener('resize', checkMobile);
		return () => window.removeEventListener('resize', checkMobile);
	}, []);

	const fetchData = async (page: number, append: boolean = false) => {
	    if (fetchUrl && !isLoading) {
		const offset = page * listCount;
		setIsAppending(append);
		setIsLoading(true);
		try {
		    const response = await api.getTableList(fetchUrl, listCount, offset);
		    // 서버가 200 으로 실패 코드를 실어 보내는 경우가 있어 code 까지 확인한다 (0·200 만 정상)
		    if (response?.code !== 0 && response?.code !== 200) {
		        throw new Error(`목록 조회 실패 (code=${response?.code})`);
		    }
		    const newData = response?.result?.list ?? [];
		    const total = response?.result?.total ?? 0;

		    if (append) {
		        setRequestData(prev => [...prev, ...newData]);
		    } else {
		        setRequestData(newData);
		    }

		    setTotalPages(Math.ceil(total / listCount));
		    setHasMore((page + 1) * listCount < total);
		    setHasError(false);
		    failedRequestRef.current = null;
		    // 받아온 페이지가 화면에 올라갔으니 이제 페이지네이션도 이 번호를 켠다
		    setDisplayedPage(page);

		    // 초기 로드(offset=0)일 때만 콜백 호출
		    if (offset === 0) {
		        initialResultRef.current = response?.result ?? null;
		        if (onInitialDataFetched) {
		            onInitialDataFetched(response?.result);
		        }
		    }
		} catch (error) {
		    console.error(error);
		    failedRequestRef.current = { page, append };
		    setHasError(true);
		    // displayedPage 는 건드리지 않는다 — 화면에 남아 있는 건 직전에 성공한 페이지이므로 페이지네이션도 그 번호를 유지해야 한다.
		} finally {
		    setIsLoading(false);
		    setHasFetched(true);
		}
	    }
	};

	/** 실패한 바로 그 요청을 다시 보낸다 (모바일 2페이지 이후 실패면 append 로) */
	const retry = () => {
	    const failed = failedRequestRef.current;
	    if (!failed) return;
	    setHasError(false);
	    fetchData(failed.page, failed.append);
	};

	/**
	 * 페이지 이동. 실패한 페이지를 다시 누른 경우 currentPage 값이 그대로라 로드 effect 가 돌지 않으므로 직접 다시 받는다.
	 */
	const handlePageChange = (page: number) => {
	    if (page === currentPage) {
		fetchData(page, false);
		return;
	    }
	    setCurrentPage(page);
	};

	// 인피니티 스크롤을 위한 Intersection Observer
	useEffect(() => {
		// 실패 상태에서는 관찰을 멈춘다 — 그대로 두면 실패한 페이지를 계속 다시 요청한다
		if (!isMobile || !fetchUrl || !hasFetched || !hasMore || isLoading || hasError) return;

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting && hasMore && !isLoading) {
					setCurrentPage(prev => prev + 1);
				}
			},
			{ threshold: 0.1 }
		);

		if (observerTarget.current) {
			observer.observe(observerTarget.current);
		}

		return () => {
			if (observerTarget.current) {
				observer.unobserve(observerTarget.current);
			}
		};
	}, [isMobile, fetchUrl, hasFetched, hasMore, isLoading, hasError]);

	/** initialData가 있을 때 데이터 설정 */
	useEffect(() => {
	    if (initialData) {
		setRequestData(initialData);
		setTotalPages(Math.ceil(initialData.length / listCount));
	    }
	}, [initialData, listCount]);

	/** fetchUrl 값이 있을 때 데이터 로드 */
	useEffect(() => {
	    if (!initialData && fetchUrl) {
		const restoredAt = restoredAtRef.current;
		if (restoredAt && restoredAt.url === fetchUrl && restoredAt.page === currentPage) {
		    // 복원된 목록 그대로 — 다시 받지 않는다
		    return;
		}
		restoredAtRef.current = null;
		// 모바일에서는 데이터를 누적, 데스크톱에서는 교체
		fetchData(currentPage, isMobile && currentPage > 0);
	    }
	}, [currentPage, fetchUrl]);

	/** 복원 시에도 화면의 파생 상태(배너·카운트 등)를 채우도록 첫 페이지 응답을 다시 전달 */
	useEffect(() => {
	    if (snapshot && onInitialDataFetched && snapshot.initialResult != null) {
		onInitialDataFetched(snapshot.initialResult);
	    }
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/** 목록 상태가 바뀔 때마다 최신 상태를 기억해 둔다 (참조만 보관) */
	useEffect(() => {
	    // 실패 상태의 목록은 기억하지 않는다 — 되돌아왔을 때 불완전한 목록이 되살아나면 안 된다
	    if (!fetchUrl || initialData || !hasFetched || hasError) return;
	    save({ requestData, currentPage: displayedPage, totalPages, hasMore, initialResult: initialResultRef.current });
	}, [requestData, displayedPage, totalPages, hasMore, hasFetched, hasError, fetchUrl, initialData, save]);

	// 실패는 "0건" 과 구분해서 보여준다 — 조용히 빈 목록을 그리면 사용자는 글이 없는 줄 알고 떠난다
	const showEmpty = emptyMessage != null && hasFetched && !isLoading && !hasError && requestData.length === 0;
	const pageLoading = isLoading && !isAppending && loadingPlacement === 'page';

	return (
	    <>
		{/* PC 페이지 교체 중에는 이전 행의 높이만 유지한다. 중앙 로더와 글이 겹치거나 이전 글을 누르지 않게 한다. */}
		{/* 빈 안내(emptyMessage)를 넘긴 목록은 0건일 때 안쪽 표를 그리지 않는다 — 표가 자체 빈 문구를 또 그려 빈 상태가 두 번 나왔다(내가 쓴 글, 2026-10-06) */}
		{/* 실패했는데 그린 글이 없으면 안쪽 표를 그리지 않는다 — 표의 빈 문구("아직 게시글이 없습니다")와 실패 안내가 같이 나왔다(2026-10-11) */}
		{!showEmpty && !(hasError && requestData.length === 0) && !(fetchUrl && !initialData && !hasFetched && requestData.length === 0) && (
		    <div style={pageLoading ? { visibility: 'hidden' } : undefined} aria-hidden={pageLoading || undefined}>
			<InnerTable headers={headers} data={requestData} onClick={onClick} />
		    </div>
		)}

		{showEmpty && <EmptyState>{emptyMessage}</EmptyState>}

		{hasError && !isLoading && (
		    <PageState
			title="목록을 불러오지 못했습니다"
			description="네트워크 상태를 확인한 뒤 다시 시도해 주세요."
			error
			onRetry={retry}
		    />
		)}

		{/* 모바일 인피니티 스크롤을 위한 observer target */}
		{isMobile && fetchUrl && hasMore && !hasError && (
		    <div ref={observerTarget} style={{ height: '20px' }} />
		)}

		{/* 주 목록 조회는 모바일/PC 모두 화면 중앙. 모바일 더보기는 목록 아래. */}
		{isLoading && (loadingPlacement === 'page' || isMobile) && (
		    <LoadingSpinner
			placement={isAppending ? 'inline' : loadingPlacement}
			minHeight={pageLoading ? (requestData.length > 0 ? '0' : undefined) : '160px'}
		    />
		)}

		{/* 데스크톱 페이지네이션 */}
		{!initialData && fetchUrl && (
		    <PaginationWrapper>
			<Pagination
			    currentPage={displayedPage}
			    totalPages={totalPages}
			    windowSize={windowSize}
			    onPageChange={handlePageChange}
			/>
		    </PaginationWrapper>
		)}

		{/* 바깥 레이아웃 여백과 중복되지 않을 때만 추가한다. */}
		{bottomSpacer && <div style={{ height: '50px' }} />}
	    </>
	);
    };

export default TableList;
